import React, {CSSProperties, FC} from 'react'
import {LinkLineStyle, LinkPosition, useAppDispatch, WidgetLinkData} from '@dv5/domain'
import {messagesService} from '@dv5/api'
import {ColorPicker} from '../common/ColorPicker'
import './WidgetLinkToolbar.less'

interface Props {
    canvasId: string
    linkId: string
    data: WidgetLinkData
    toolbarStyles: CSSProperties
    startWidgetId: string
    endWidgetId: string
}

interface ChangeWidgetLinkData {
    canvasId: string
    linkId: string
    patchedPaths: Array<string>
    data: WidgetLinkData
}

const COLOR_PRESETS = [
    '#000000', '#ffffff', '#ff0000', '#00ff00', '#0000ff',
    '#ffff00', '#ff00ff', '#00ffff', '#a6ccf5', '#333333'
]

const POSITIONS: LinkPosition[] = ['top', 'right', 'bottom', 'left']

const POSITION_LABELS: Record<LinkPosition, string> = {
    top: 'T',
    right: 'R',
    bottom: 'B',
    left: 'L',
}

const LINE_STYLES: LinkLineStyle[] = ['solid', 'dashed', 'dotted']

const LINE_STYLE_LABELS: Record<LinkLineStyle, string> = {
    solid: 'Solid',
    dashed: 'Dashed',
    dotted: 'Dotted',
}

export const WidgetLinkToolbar: FC<Props> = ({
    canvasId,
    linkId,
    data,
    toolbarStyles,
    startWidgetId,
    endWidgetId,
}) => {
    const dispatch = useAppDispatch()

    const updateData = (newData: Partial<WidgetLinkData>) => {
        const updatedData = {...data, ...newData}
        messagesService.send<ChangeWidgetLinkData>('canvas.change-widget-link-data', {
            canvasId,
            linkId,
            patchedPaths: [],
            data: updatedData,
        })
    }

    const handleStartPositionChange = (event: React.MouseEvent<HTMLButtonElement>, position: LinkPosition) => {
        event.preventDefault()
        event.stopPropagation()
        updateData({startPosition: position})
    }

    const handleEndPositionChange = (event: React.MouseEvent<HTMLButtonElement>, position: LinkPosition) => {
        event.preventDefault()
        event.stopPropagation()
        updateData({endPosition: position})
    }

    const handleColorChange = (color: string) => {
        updateData({color})
    }

    const handleLineStyleChange = (event: React.MouseEvent<HTMLButtonElement>, lineStyle: LinkLineStyle) => {
        event.preventDefault()
        event.stopPropagation()
        updateData({lineStyle})
    }

    const currentLineStyle: LinkLineStyle = data.lineStyle || 'solid'

    return (
        <div className='widget-link-toolbar' style={toolbarStyles}>
            <div className='widget-link-toolbar__section'>
                <div className='widget-link-toolbar__label'>Start</div>
                <div className='widget-link-toolbar__positions'>
                    {POSITIONS.map((pos) => (
                        <button
                            key={pos}
                            className={`widget-link-toolbar__position ${data.startPosition === pos ? 'widget-link-toolbar__position--active' : ''}`}
                            onMouseDown={event => handleStartPositionChange(event, pos)}
                            aria-label={`Start position ${pos}`}
                            type='button'
                        >
                            {POSITION_LABELS[pos]}
                        </button>
                    ))}
                </div>
            </div>
            <div className='widget-link-toolbar__divider' />
            <div className='widget-link-toolbar__section'>
                <div className='widget-link-toolbar__label'>End</div>
                <div className='widget-link-toolbar__positions'>
                    {POSITIONS.map((pos) => (
                        <button
                            key={pos}
                            className={`widget-link-toolbar__position ${data.endPosition === pos ? 'widget-link-toolbar__position--active' : ''}`}
                            onMouseDown={event => handleEndPositionChange(event, pos)}
                            aria-label={`End position ${pos}`}
                            type='button'
                        >
                            {POSITION_LABELS[pos]}
                        </button>
                    ))}
                </div>
            </div>
            <div className='widget-link-toolbar__divider' />
            <div className='widget-link-toolbar__section'>
                <div className='widget-link-toolbar__label'>Style</div>
                <div className='widget-link-toolbar__line-styles'>
                    {LINE_STYLES.map((style) => (
                        <button
                            key={style}
                            className={`widget-link-toolbar__position widget-link-toolbar__line-style ${currentLineStyle === style ? 'widget-link-toolbar__position--active' : ''}`}
                            onMouseDown={event => handleLineStyleChange(event, style)}
                            aria-label={`Line style ${style}`}
                            type='button'
                        >
                            {LINE_STYLE_LABELS[style]}
                        </button>
                    ))}
                </div>
            </div>
            <div className='widget-link-toolbar__divider' />
            <ColorPicker
                value={data.color || '#000000'}
                presets={COLOR_PRESETS}
                onChange={handleColorChange}
            />
        </div>
    )
}
